import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, CheckCircle2, ArrowRight, ShieldAlert } from "lucide-react";

interface UpgradePackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleName: string;
  currentPackageName?: string;
}

const MODULE_DESCRIPTIONS: Record<string, { title: string; desc: string; highlights: string[] }> = {
  finance: {
    title: "Finance & Accounting Suite",
    desc: "Complete double-entry general ledger, automated GST return filings, bank reconciliation, and cash flow management.",
    highlights: ["General Ledger & Chart of Accounts", "E-Invoicing & E-Way Bill Auto-Generation", "Bank Account Imports & Reconciliation", "MIS Financial Reports & Profit/Loss"],
  },
  crm: {
    title: "Sales CRM & Lead Pipeline",
    desc: "Comprehensive sales CRM to manage lead capture, deal stages, quotations, sales orders, and customer accounts.",
    highlights: ["Interactive Drag-and-Drop Pipeline", "Quotation & Commercial Sales Orders", "Customer Portal & Account History", "Sales Target & Commission Tracking"],
  },
  manufacturing: {
    title: "Manufacturing & Production MRP",
    desc: "End-to-end production management with multi-level Bill of Materials (BOM), work centres, and MRP scheduling.",
    highlights: ["Multi-Level BOM Explosion", "Work Centre Capacity Planning", "Production Order Tracking", "Material Requirement Planning (MRP)"],
  },
  quality: {
    title: "Quality Control & Assurance",
    desc: "Quality inspection checklists, Non-Conformance Reports (NCR), CAPA action plans, and Certificate of Analysis (COA).",
    highlights: ["Inward & Production Quality Gates", "Non-Conformance & CAPA Workflow", "Inspection Templates & Thresholds", "Supplier Quality Rating Scorecards"],
  },
  inventory: {
    title: "Warehouse & Stock Management",
    desc: "Multi-warehouse stock batch tracking, automated reorder triggers, stock movements, and valuation reports.",
    highlights: ["Multi-Location Warehouse Control", "Batch & Expiry Date Tracking", "Automated Reorder Level Triggers", "Stock Transfer & Valuation Log"],
  },
  procurement: {
    title: "Procurement & Purchase Orders",
    desc: "Purchase indents, RFQ generation, supplier scorecards, 3-way match validation, and vendor bill approvals.",
    highlights: ["Purchase Indents & Blanket POs", "Supplier RFQ & Quotation Comparison", "3-Way Invoice Matching", "Vendor Portal & Bill Approvals"],
  },
  operations: {
    title: "Operations & Project Management",
    desc: "Project task breakdown, field service dispatching, timesheet logs, and ticket tracking.",
    highlights: ["Gantt & Kanban Project Planning", "Field Operations & Dispatching", "Employee Timesheet Logging", "Operational Ticket Management"],
  },
  ats: {
    title: "ATS & Recruitment Portal",
    desc: "Job posting syndication, candidate pipeline tracking, interview scheduling, and offer letter generation.",
    highlights: ["Applicant Tracking Pipeline", "Interview Feedback Scorecards", "Automated Offer Letter Builder", "Candidate Resume Bank"],
  },
  bi: {
    title: "BI Analytics & Scorecards",
    desc: "Executive BI dashboards, KPI scorecards, customizable widget layouts, and data trend analytics.",
    highlights: ["Real-time Revenue & Cost KPIs", "Customizable Dashboard Layouts", "Automated Scheduled Email PDF Reports", "Cross-Module Data Insights"],
  },
};

export const UpgradePackageModal: React.FC<UpgradePackageModalProps> = ({
  isOpen,
  onClose,
  moduleName,
  currentPackageName = "Current Tier",
}) => {
  const normalizedKey = moduleName.toLowerCase();
  const info = MODULE_DESCRIPTIONS[normalizedKey] || {
    title: `${moduleName.toUpperCase()} Module`,
    desc: "Access advanced enterprise operations, reporting, and automated workflows by upgrading your SaaS tier.",
    highlights: [
      "Advanced Enterprise Workflows",
      "Role-based Access & Controls",
      "Real-time Dashboard & Analytics",
      "Priority 24/7 Dedicated Support",
    ],
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[540px] p-0 overflow-hidden border border-border/80 shadow-2xl rounded-2xl bg-card">
        {/* Top Gradient Banner */}
        <div className="bg-gradient-to-r from-amber-500/20 via-primary/20 to-purple-600/20 p-6 border-b border-border/50 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 mb-3">
            <Lock className="w-3.5 h-3.5" />
            <span>Panel Locked • Subscription Upgrade Required</span>
          </div>

          <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
            <span>{info.title}</span>
            <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
          </DialogTitle>

          <DialogDescription className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            This module is not included in your organization's active <strong className="text-foreground">{currentPackageName}</strong> plan.
          </DialogDescription>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {info.desc}
          </p>

          <div className="bg-muted/40 rounded-xl p-4 border border-border/50 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              What you unlock with an upgrade:
            </h4>
            <div className="grid grid-cols-1 gap-2.5">
              {info.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{highlight}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-300">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>Contact your Organization Super Admin or Upgrade your plan to instantly unlock this panel.</span>
          </div>
        </div>

        {/* Modal Footer */}
        <DialogFooter className="p-5 bg-muted/20 border-t border-border/50 flex sm:justify-between items-center gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              window.open("/pricing", "_blank", "noopener,noreferrer");
            }}
            className="rounded-xl bg-gradient-to-r from-amber-500 to-primary text-primary-foreground hover:opacity-95 shadow-md flex items-center gap-2"
          >
            <span>Explore Upgrade Plans</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
